"use client";
import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  login as loginApi,
  logout as logoutApi,
  register as registerApi,
  impersonate as impersonateApi,
} from "../services/auth";
import api from "../services/api";
import { getSettings, clearSettingsCache, updateLocalStorage } from "../services/settings";
import { getDefaultLandingPath } from "../lib/packageModules";

interface AuthContextType {
  user: any;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  signup: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  impersonate: (employeeId: number, pin?: string) => Promise<void>;
  updateUser: (data: any) => void;
  syncSettings: () => Promise<void>;
  syncSettingsToLocalStorage: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const isSuperAdminLike = (user: any) => {
  const roles = user?.roles || [];
  return roles.some((role: any) => role?.name === "superadmin" || role?.name === "agent");
};

const isEmployeeLike = (user: any) => {
  const roles = user?.roles || [];
  return roles.some((role: any) => role?.name === "employee");
};

const isPosRoute = (pathname: string) =>
  pathname === "/pos" || pathname.startsWith("/pos/");

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is authenticated
  const isAuthenticated = !!user && !!localStorage.getItem("token");

  // Validate token on mount (authenticated app only)
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      setLoading(false);
      return;
    }
    validateToken();
  }, []);

  const validateToken = async () => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Optimistically load stored user
      let currentUser = storedUser ? JSON.parse(storedUser) : null;
      if (currentUser) {
        setUser(currentUser);
      }

      // Super admin/agent does not use tenant /admin settings endpoint.
      if (currentUser && isSuperAdminLike(currentUser)) {
        return;
      }

      // Fetch fresh settings from server to ensure consistency across devices/browsers
      const settingsResponse = await getSettings();

      if (settingsResponse?.data?.settings) {
        const settings = settingsResponse.data.settings;
        const currencies = settingsResponse.data.currencies || [];
        // Use loose equality to handle string/number differences
        const currency = currencies.find((c: any) => c.id == settings.currency_id);

        // If we have a user, update their company settings
        if (currentUser) {
          const settingsAny = settings as unknown as Record<string, unknown>;
          const mergedPackage =
            settingsAny.package != null ? settingsAny.package : currentUser.company?.package;
          const updatedUser = {
            ...currentUser,
            company: {
              ...(currentUser.company || {}),
              ...settings,
              package: mergedPackage,
              currency: currency || currentUser.company?.currency,
              currency_symbol: currency?.currency_symbol || currentUser.company?.currency_symbol,
              currency_code: currency?.currency_code || currentUser.company?.currency_code,
            },
          };
          console.log("Syncing user settings from server:", updatedUser);
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser)); // Persist fresh data
        }
      }
    } catch (error) {
      console.error("Token validation/settings sync failed:", error);
      // If the token is invalid (e.g. 401), we might want to clear it,
      // but for network errors we should probably keep the local data.
      // Only clear if it's strictly an auth error, but for now let's be safe
      // and NOT wipe everything on a simple fetch error unless sure.

      // If we failed to fetch settings but have a stored user, we leave it be.
      // If we have NO user and failed... then maybe clear.
      if (!storedUser) {
        localStorage.removeItem("token");
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const syncSettings = async () => {
    try {
      if (isSuperAdminLike(user)) {
        return;
      }

      const settingsResponse = await getSettings(true); // Force fresh fetch

      if (settingsResponse?.data?.settings) {
        const settings = settingsResponse.data.settings;
        const currencies = settingsResponse.data.currencies || [];
        const currency = currencies.find((c: any) => c.id == settings.currency_id);

        setUser((prevUser: any) => {
          if (!prevUser) return null;
          const settingsAny = settings as unknown as Record<string, unknown>;
          const mergedPackage =
            settingsAny.package != null ? settingsAny.package : prevUser.company?.package;
          const updatedUser = {
            ...prevUser,
            company: {
              ...(prevUser.company || {}),
              ...settings,
              package: mergedPackage,
              currency: currency || prevUser.company?.currency,
              currency_symbol: currency?.currency_symbol || prevUser.company?.currency_symbol,
              currency_code: currency?.currency_code || prevUser.company?.currency_code,
            },
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
    } catch (error) {
      console.error("Manual settings sync failed:", error);
    }
  };

  /**
   * Syncs localStorage with the latest user data from the backend.
   * This should be called after any settings update to ensure all components
   * using useAuth() get the latest data.
   */
  const syncSettingsToLocalStorage = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      return;
    }

    try {
      if (isSuperAdminLike(user)) {
        return;
      }

      const response = await updateLocalStorage();
      if (response?.success && response?.data?.user) {
        setUser((prevUser: any) => {
          const next = {
            ...(prevUser || {}),
            ...response.data.user,
            permissions:
              response.data.user.permissions ??
              response.data.permissions ??
              prevUser?.permissions ??
              [],
            roles:
              response.data.user.roles ??
              (Array.isArray(response.data.roles)
                ? response.data.roles.map((name: string) =>
                    typeof name === "string" ? { name } : name
                  )
                : prevUser?.roles),
            ...(prevUser?.impersonation
              ? { impersonation: prevUser.impersonation }
              : {}),
          };
          localStorage.setItem("user", JSON.stringify(next));
          return next;
        });
      }
    } catch (error) {
      console.error("Failed to sync settings to localStorage:", error);
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Keep package/modules in sync when account changes happen server-side.
    const onFocusOrVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (isPosRoute(location.pathname)) return;
      syncSettingsToLocalStorage();
    };

    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [isAuthenticated, syncSettingsToLocalStorage, location.pathname]);

  const updateUser = (data: any) => {
    setUser((prevUser: any) => {
      const newUser = { ...prevUser, ...data };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  };

  const login = async (email: string, password: string, remember = true) => {
    try {
      const { user: loggedInUser, token } = await loginApi(
        email,
        password,
        remember
      );

      if (loggedInUser && token) {
        setUser(loggedInUser);
        localStorage.setItem("user", JSON.stringify(loggedInUser));
        localStorage.setItem("token", token);

        const defaultLanding = getDefaultLandingPath(loggedInUser);
        let from = location.state?.from?.pathname || defaultLanding;

        if (
          from === "/" ||
          from === "/dashboard" ||
          from === "/employee/dashboard" ||
          from === "/superadmin/dashboard"
        ) {
          from = defaultLanding;
        }

        navigate(from, { replace: true });
      }
    } catch (error) {
      throw error;
    }
  };

  const signup = async (formData: any) => {
    try {
      const { user: registeredUser, token } = await registerApi(formData);

      if (registeredUser && token) {
        setUser(registeredUser);
        localStorage.setItem("user", JSON.stringify(registeredUser));
        localStorage.setItem("token", token);

        navigate(getDefaultLandingPath(registeredUser), { replace: true });
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      clearSettingsCache();
      navigate("/login", { replace: true });
    }
  };

  const impersonate = async (employeeId: number, pin?: string) => {
    try {
      const { accessToken, user: impersonatedUser, impersonation: impersonationData } = await impersonateApi(employeeId, pin);
      const userWithImpersonation = {
        ...impersonatedUser,
        impersonation: impersonationData,
      };
      setUser(userWithImpersonation);
      localStorage.setItem("token", accessToken);
      localStorage.setItem("user", JSON.stringify(userWithImpersonation));
      navigate("/employee/dashboard", { replace: true });
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        impersonate,
        updateUser,
        syncSettings,
        syncSettingsToLocalStorage,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
